import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { api } from '../utils/api';

const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

const COMMON_CONDITIONS = ['Hypertension', 'Prediabetes', 'Diabetes', 'Sleep Apnea', 'Obesity', 'Heart Disease', 'High Cholesterol'];

const PROVIDERS = ['Dr. Sarah Chen', 'Dr. Michael Patel', 'Nurse Elena Rodriguez'];

const AddPatientModal = ({ open, onClose, onSuccess, editPatient = null }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: editPatient?.first_name || '',
    last_name: editPatient?.last_name || '',
    email: editPatient?.email || '',
    phone: editPatient?.phone || '',
    date_of_birth: editPatient?.date_of_birth || '',
    city: editPatient?.city || '',
    state: editPatient?.state || '',
    zip_code: editPatient?.zip_code || '',
    condition_tags: editPatient?.condition_tags || [],
    assigned_provider: editPatient?.assigned_provider || '',
  });
  const [conditionInput, setConditionInput] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addCondition = (condition) => {
    if (condition && !formData.condition_tags.includes(condition)) {
      setFormData({ ...formData, condition_tags: [...formData.condition_tags, condition] });
      setConditionInput('');
    }
  };

  const removeCondition = (condition) => {
    setFormData({
      ...formData,
      condition_tags: formData.condition_tags.filter((c) => c !== condition),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editPatient) {
        await api.updatePatient(editPatient.id, formData);
        toast.success('Patient updated successfully!');
      } else {
        await api.createPatient(formData);
        toast.success('Patient created successfully!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save patient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-patient-modal">
        <DialogHeader>
          <DialogTitle>{editPatient ? 'Edit Patient Information' : 'Add New Patient'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                data-testid="first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                data-testid="last-name-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_of_birth">Date of Birth *</Label>
            <Input
              id="date_of_birth"
              name="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={handleChange}
              required
              data-testid="dob-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                data-testid="city-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Select
                value={formData.state}
                onValueChange={(value) => setFormData({ ...formData, state: value })}
              >
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip_code">ZIP Code *</Label>
              <Input
                id="zip_code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                required
                data-testid="zip-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Health Conditions</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.condition_tags.map((condition) => (
                <span
                  key={condition}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                  data-testid={`condition-tag-${condition}`}
                >
                  {condition}
                  <button
                    type="button"
                    onClick={() => removeCondition(condition)}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={conditionInput}
                onChange={(e) => setConditionInput(e.target.value)}
                placeholder="Type condition"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCondition(conditionInput);
                  }
                }}
                data-testid="condition-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addCondition(conditionInput)}
                data-testid="add-condition-btn"
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-sm text-gray-600">Common:</span>
              {COMMON_CONDITIONS.map((condition) => (
                <button
                  key={condition}
                  type="button"
                  onClick={() => addCondition(condition)}
                  className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  + {condition}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assigned_provider">Assigned Provider *</Label>
            <Select
              value={formData.assigned_provider}
              onValueChange={(value) => setFormData({ ...formData, assigned_provider: value })}
            >
              <SelectTrigger data-testid="provider-select">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((provider) => (
                  <SelectItem key={provider} value={provider}>
                    {provider}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-full"
              style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
              disabled={loading}
              data-testid="submit-patient-btn"
            >
              {loading ? 'Saving...' : editPatient ? 'Save Changes' : 'Create Patient'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPatientModal;
